import * as React from "react";

import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Tailwind utilities that hide the native number-input spin buttons so the
 * field keeps a clean text-field appearance (arrows are handled via keyboard).
 */
export const HIDE_NUMBER_SPINNERS =
  "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none";

export interface NumberFieldProps
  extends Omit<
    React.ComponentProps<typeof Input>,
    "value" | "onChange" | "type" | "min" | "max" | "step" | "defaultValue"
  > {
  /** Current committed value. */
  value: number;
  /** Called with the parsed numeric value while editing and on commit. */
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Increment used for the ArrowUp/ArrowDown keys. Defaults to 1. */
  step?: number;
  /**
   * When true (default) the value is clamped into [min, max] on blur. Set to
   * false for fields that surface their own validation error for out-of-range
   * values, so the typed value is preserved on blur instead of being clamped.
   */
  clampOnBlur?: boolean;
  /**
   * Formats the committed value for display when the field is not being
   * edited (e.g. `(v) => v.toFixed(2)`). Defaults to `String`.
   */
  format?: (value: number) => string;
}

/**
 * A numeric `<input>` that behaves the way users expect:
 *  - auto-selects its contents on focus (click to overwrite),
 *  - can be cleared entirely while editing (the keystroke is never discarded),
 *  - resets to the last valid value on blur if left empty,
 *  - supports ArrowUp/ArrowDown increment/decrement clamped to min/max,
 *  - hides the native spin buttons for a consistent appearance.
 */
export function NumberField({
  value,
  onChange,
  min,
  max,
  step = 1,
  clampOnBlur = true,
  format,
  className,
  onFocus,
  onBlur,
  onKeyDown,
  ...props
}: NumberFieldProps) {
  const isInteger = Number.isInteger(step);
  const display = React.useCallback(
    (n: number) => (format ? format(n) : String(n)),
    [format],
  );
  const [draft, setDraft] = React.useState(() => display(value));
  const focusedRef = React.useRef(false);

  // Keep the displayed draft in sync with the committed value while the field
  // is not being edited (e.g. programmatic resets / discards).
  React.useEffect(() => {
    if (!focusedRef.current) setDraft(display(value));
  }, [value, display]);

  const parse = React.useCallback(
    (raw: string) => (isInteger ? parseInt(raw, 10) : parseFloat(raw)),
    [isInteger],
  );

  const clamp = React.useCallback(
    (n: number) => {
      let result = n;
      if (min !== undefined) result = Math.max(min, result);
      if (max !== undefined) result = Math.min(max, result);
      return result;
    },
    [min, max],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setDraft(raw);
    if (raw === "") return; // allow clearing while editing
    const parsed = parse(raw);
    if (!Number.isNaN(parsed)) onChange(parsed);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    focusedRef.current = true;
    e.target.select();
    onFocus?.(e);
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    focusedRef.current = false;
    const parsed = parse(draft);
    if (draft === "" || Number.isNaN(parsed)) {
      setDraft(display(value));
    } else if (clampOnBlur) {
      const clamped = clamp(parsed);
      setDraft(display(clamped));
      if (clamped !== value) onChange(clamped);
    } else {
      setDraft(display(parsed));
    }
    onBlur?.(e);
  };

  const bump = (delta: number) => {
    const parsedDraft = parse(draft);
    const current = Number.isNaN(parsedDraft) ? value : parsedDraft;
    const next = clamp(current + delta);
    setDraft(display(next));
    if (next !== value) onChange(next);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowUp") {
      e.preventDefault();
      bump(step);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      bump(-step);
    }
    onKeyDown?.(e);
  };

  return (
    <Input
      type="number"
      inputMode={isInteger ? "numeric" : "decimal"}
      min={min}
      max={max}
      step={step}
      value={draft}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      className={cn(HIDE_NUMBER_SPINNERS, className)}
      {...props}
    />
  );
}
